<template>
  <SidebarNavigation :uri="uri">
    <div class="lead-form-builder min-h-screen bg-gray-50">
      <!-- Header -->
      <div class="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
        <div class="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 min-w-0 flex-1">
          <h1 class="text-xl sm:text-2xl font-bold text-gray-900 truncate">Smart Lead Capture Form Generator</h1>
          <div v-if="currentForm" class="flex items-center gap-2">
            <span class="text-xs sm:text-sm text-gray-500 truncate">
              Editing: {{ currentForm.title }}
            </span>
            <span v-if="hasUnsavedChanges" class="text-xs text-orange-600 font-medium">• Unsaved changes</span>
            <span v-else-if="lastSaved" class="text-xs text-gray-400">• Saved {{ formatTime(lastSaved) }}</span>
            <span v-if="autoSaving" class="text-xs text-blue-600 font-medium">• Auto-saving...</span>
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <button
            v-if="currentForm"
            @click="saveForm"
            :disabled="saving"
            class="flex-1 sm:flex-none px-4 py-2 text-sm sm:text-base bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
          >
            {{ saving ? 'Saving...' : 'Save Form' }}
          </button>
          <button
            v-if="currentForm"
            @click="viewLeads"
            class="flex-1 sm:flex-none px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors shadow-sm hover:shadow-md"
          >
            View Leads
          </button>
          <button
            v-if="currentForm"
            @click="viewForm"
            class="flex-1 sm:flex-none px-4 py-2 text-sm sm:text-base bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 active:bg-gray-400 transition-colors shadow-sm hover:shadow-md"
          >
            View Form
          </button>
        </div>
      </div>

      <div class="flex flex-col lg:flex-row h-[calc(100vh-73px)] sm:h-[calc(100vh-81px)]">
        <!-- Left Sidebar: Form Fields -->
        <div class="w-full lg:w-80 bg-white border-r border-gray-200 overflow-y-auto p-4 border-b lg:border-b-0">
          <div v-if="currentForm">
            <div class="mb-6 pb-6 border-b border-gray-200">
              <h3 class="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
                <icon name="settings" class="w-5 h-5 text-green-600" />
                Form Settings
              </h3>

              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Form Title</label>
                  <input
                    v-model="currentForm.title"
                    type="text"
                    class="form-input w-full border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Contact Form"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    v-model="currentForm.description"
                    class="form-input w-full border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    rows="3"
                    placeholder="Optional description"
                  ></textarea>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Theme Color</label>
                  <div class="flex items-center gap-3">
                    <input
                      v-if="currentForm"
                      v-model="currentForm.theme_color"
                      type="color"
                      class="w-16 h-10 rounded-lg border-2 border-gray-300 cursor-pointer hover:border-green-500 transition-colors"
                    />
                    <div class="flex-1">
                      <input
                        v-if="currentForm"
                        type="text"
                        :value="currentForm.theme_color"
                        @input="currentForm.theme_color = $event.target.value; validateThemeColor()"
                        @blur="validateThemeColor()"
                        :class="[
                          'w-full px-3 py-2 border-2 rounded-lg focus:ring-2 text-sm font-mono',
                          validationErrors.theme_color ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-green-500 focus:border-green-500'
                        ]"
                        placeholder="#3b82f6"
                      />
                      <p v-if="validationErrors.theme_color" class="text-xs text-red-500 mt-1">{{ validationErrors.theme_color }}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Success Message</label>
                  <input
                    v-model="currentForm.success_message"
                    type="text"
                    class="form-input w-full border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Thank you for your submission!"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Redirect URL <span class="text-gray-500 font-normal">(Optional)</span></label>
                  <div>
                    <input
                      v-model="currentForm.redirect_url"
                      type="url"
                      @blur="validateRedirectUrl()"
                      :class="[
                        'form-input w-full border-2 rounded-lg focus:ring-2',
                        validationErrors.redirect_url ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-green-500 focus:border-green-500'
                      ]"
                      placeholder="https://example.com/thank-you"
                    />
                    <p v-if="validationErrors.redirect_url" class="text-xs text-red-500 mt-1">{{ validationErrors.redirect_url }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="mb-6 pb-6 border-b border-gray-200">
              <h3 class="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
                <icon name="plus" class="w-5 h-5 text-green-600" />
                Available Fields
              </h3>
              <div class="space-y-2">
                <button
                  v-for="fieldType in availableFields"
                  :key="fieldType.type"
                  @click="addField(fieldType)"
                  class="w-full text-left px-4 py-3 bg-gray-50 hover:bg-green-50 border border-gray-200 hover:border-green-400 rounded-lg transition-all flex items-center gap-3 shadow-sm hover:shadow-md"
                >
                  <icon :name="fieldType.icon" class="w-5 h-5 text-gray-600" />
                  <div class="flex-1">
                    <div class="font-medium text-gray-900">{{ fieldType.label }}</div>
                    <div class="text-xs text-gray-500">{{ fieldType.description }}</div>
                  </div>
                </button>
              </div>
            </div>

            <!-- Form Fields List -->
            <div v-if="currentForm.fields && currentForm.fields.length > 0">
              <h3 class="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
                <icon name="category" class="w-5 h-5 text-green-600" />
                Form Fields ({{ currentForm.fields.length }})
              </h3>
              <div class="space-y-2">
                <div
                  v-for="(field, index) in currentForm.fields"
                  :key="field.id || index"
                  class="p-3 rounded-lg border-2 border-gray-200 hover:border-green-300 hover:bg-gray-50 transition-all"
                >
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                      <span class="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded">{{ index + 1 }}</span>
                      <span class="font-medium text-gray-900">{{ field.label }}</span>
                      <span class="text-xs text-gray-500">({{ field.type }})</span>
                      <span v-if="field.required" class="text-xs text-red-500 font-semibold">*</span>
                    </div>
                    <button
                      @click="removeField(index)"
                      class="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                      title="Remove field"
                    >
                      <icon name="trash" class="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Center: Live Preview -->
        <div class="flex-1 overflow-y-auto bg-gray-100 p-4 sm:p-6 lg:p-8">
          <div v-if="!currentForm" class="flex items-center justify-center h-full">
            <div class="text-center bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md">
              <icon name="page" class="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 class="text-xl font-semibold text-gray-700 mb-2">No form selected</h3>
              <p class="text-gray-500 mb-6">Create a new lead capture form to get started</p>
              <button
                @click="showCreateModal = true"
                class="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-md hover:shadow-lg font-semibold flex items-center gap-2 mx-auto"
              >
                <icon name="plus" class="w-5 h-5 text-white fill-white" />
                Create New Form
              </button>
            </div>
          </div>

          <div v-else class="max-w-2xl mx-auto bg-white shadow-lg rounded-lg p-6 sm:p-8">
            <div class="mb-6 pb-4 border-b border-gray-200">
              <h3 class="text-xl font-semibold mb-2 text-gray-800">{{ currentForm.title }}</h3>
              <p v-if="currentForm.description" class="text-gray-600 text-sm">{{ currentForm.description }}</p>
            </div>

            <div class="space-y-4">
              <div
                v-for="(field, index) in currentForm.fields"
                :key="field.id || index"
                class="relative group"
              >
                <div class="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <button
                    @click="removeField(index)"
                    class="p-1 bg-red-500 text-white rounded shadow-sm hover:bg-red-600"
                    title="Remove Field"
                  >
                    <icon name="trash" class="w-4 h-4" />
                  </button>
                </div>

                <label class="block text-sm font-medium text-gray-700 mb-1">
                  {{ field.label }}
                  <span v-if="field.required" class="text-red-500">*</span>
                </label>
                <input
                  v-if="field.type === 'text' || field.type === 'email' || field.type === 'tel'"
                  :type="field.type"
                  :placeholder="field.placeholder || `Enter ${field.label.toLowerCase()}`"
                  class="form-input w-full"
                  :required="field.required"
                />
                <textarea
                  v-else-if="field.type === 'textarea'"
                  :placeholder="field.placeholder || `Enter ${field.label.toLowerCase()}`"
                  class="form-input w-full"
                  rows="4"
                  :required="field.required"
                ></textarea>
              </div>
            </div>

            <button
              v-if="currentForm && currentForm.theme_color"
              class="w-full mt-6 px-6 py-3 rounded-lg font-semibold text-white transition-all shadow-md hover:shadow-lg active:opacity-90"
              :style="{ backgroundColor: currentForm.theme_color }"
            >
              Submit Form
            </button>
            <button
              v-else
              class="w-full mt-6 px-6 py-3 rounded-lg font-semibold text-white transition-all shadow-md hover:shadow-lg active:opacity-90 bg-gray-400"
            >
              Submit Form
            </button>
            <p class="text-xs text-gray-500 text-center mt-3">
              This is a preview of how your form will appear to visitors
            </p>
          </div>
        </div>

        <!-- Right Sidebar: Forms List -->
        <div class="w-full lg:w-64 bg-white border-t lg:border-t-0 lg:border-l border-gray-200 overflow-y-auto p-4">
          <div class="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <icon name="file" class="w-5 h-5 text-green-600" />
              Your Forms
            </h2>
            <button
              @click="showCreateModal = true"
              class="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors shadow-sm hover:shadow-md"
              title="Create New Form"
            >
              <icon name="plus" class="w-5 h-5" />
            </button>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="space-y-2">
            <div v-for="n in 3" :key="n" class="p-4 rounded-lg border-2 border-gray-200 animate-pulse">
              <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div class="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>

          <div v-else-if="formsList.length === 0" class="text-center py-8 text-gray-400">
            <icon name="file" class="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p class="text-sm">No forms yet</p>
            <p class="text-xs mt-1">Create your first form to get started</p>
          </div>

          <div v-else class="space-y-2">
            <div
              v-for="form in formsList"
              :key="form.id"
              @click="loadForm(form.id)"
              :class="[
                'p-4 rounded-lg border-2 cursor-pointer transition-all',
                currentForm && currentForm.id === form.id
                  ? 'border-green-500 bg-green-50 shadow-md'
                  : 'border-gray-200 hover:border-green-300 hover:bg-gray-50 hover:shadow-sm'
              ]"
            >
              <div class="font-medium text-gray-900 mb-1">{{ form.title }}</div>
              <div class="text-xs text-gray-500 mb-3">{{ form.created_at }}</div>
              <div class="flex flex-wrap items-center gap-2 text-xs">
                <span class="bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">{{ form.views }} views</span>
                <span class="bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">{{ form.submissions }} submissions</span>
              </div>
              <div class="text-xs text-gray-600 mt-2 font-medium">
                {{ form.conversion_rate }}% conversion rate
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Create Form Modal -->
      <div
        v-if="showCreateModal"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        @click.self="showCreateModal = false"
      >
        <div class="bg-white rounded-lg p-6 w-full max-w-md">
          <h3 class="text-xl font-semibold mb-4">Create New Lead Form</h3>
          <input
            v-model="newFormTitle"
            @keyup.enter="createForm"
            type="text"
            placeholder="Form Title"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-green-500"
            autofocus
          />
          <div class="flex gap-3 justify-end">
            <button
              @click="showCreateModal = false"
              class="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              @click="createForm"
              :disabled="!newFormTitle || creating"
              class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ creating ? 'Creating...' : 'Create' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </SidebarNavigation>
</template>

<script>
import Layout from '@/Shared/ToolLayout'
import Icon from '@/Shared/Icon.vue'
import SidebarNavigation from '../../Shared/SidebarNavigation.vue'
import { validateHexColor, validateURL } from '../../utils/validation.js'
import { saveToStorage, loadFromStorage } from '../../utils/storage.js'

export default {
  layout: Layout,
  components: {
    Icon,
    SidebarNavigation,
  },
  props: {
    forms: {
      type: Array,
      default: () => []
    },
    uri: {
      type: String,
      default: 'lead-form'
    },
    title: {
      type: String,
      default: 'Smart Lead Capture Form Generator'
    }
  },
  data() {
    return {
      formsList: this.forms || [],
      currentForm: null,
      saving: false,
      creating: false,
      showCreateModal: false,
      newFormTitle: '',
      loading: false,
      loadingForm: false,
      autoSaving: false,
      lastSaved: null,
      hasUnsavedChanges: false,
      autoSaveTimer: null,
      validationErrors: {},
      availableFields: [
        {
          type: 'text',
          label: 'Full Name',
          description: 'Text input for name',
          icon: 'user'
        },
        {
          type: 'email',
          label: 'Email',
          description: 'Email address input',
          icon: 'email'
        },
        {
          type: 'tel',
          label: 'Phone',
          description: 'Phone number input',
          icon: 'phone'
        },
        {
          type: 'text',
          label: 'Company',
          description: 'Company name input',
          icon: 'building'
        },
        {
          type: 'textarea',
          label: 'Custom Message',
          description: 'Multi-line text area',
          icon: 'message'
        }
      ]
    }
  },
  created() {
    if (this.formsList.length > 0) {
      this.loadForm(this.formsList[0].id)
    }
  },
  mounted() {
    // Load forms list from cache if available
    const cachedForms = loadFromStorage('forms_list')
    if (cachedForms && cachedForms.length > 0 && this.formsList.length === 0) {
      this.formsList = cachedForms
    }

    // Save current forms list to cache
    if (this.formsList.length > 0) {
      saveToStorage('forms_list', this.formsList)
    }
  },
  methods: {
    validateThemeColor() {
      if (this.currentForm && this.currentForm.theme_color) {
        const result = validateHexColor(this.currentForm.theme_color)
        if (!result.valid) {
          this.validationErrors.theme_color = result.message
        } else {
          delete this.validationErrors.theme_color
        }
      } else {
        delete this.validationErrors.theme_color
      }
    },
    validateRedirectUrl() {
      if (this.currentForm && this.currentForm.redirect_url) {
        const result = validateURL(this.currentForm.redirect_url)
        if (!result.valid) {
          this.validationErrors.redirect_url = result.message
        } else {
          delete this.validationErrors.redirect_url
          // Normalize URL
          if (result.normalized) {
            this.currentForm.redirect_url = result.normalized
          }
        }
      } else {
        delete this.validationErrors.redirect_url
      }
    },
    async loadForm(formId) {
      this.loadingForm = true
      try {
        // Try to load from localStorage first for faster initial render
        const cachedData = loadFromStorage(`form_${formId}`)
        if (cachedData && cachedData.form) {
          this.currentForm = {
            id: cachedData.form.id,
            slug: cachedData.form.slug,
            title: cachedData.form.title,
            description: cachedData.form.description || '',
            fields: cachedData.form.fields || [],
            theme_color: cachedData.form.theme_color || '#17803d',
            redirect_url: cachedData.form.redirect_url || null,
            success_message: cachedData.form.success_message || 'Thank you for your submission!',
            is_active: cachedData.form.is_active !== undefined ? cachedData.form.is_active : true
          }
        }

        const response = await this.axios.get(this.route('lead-forms.edit', formId))
        if (response.data && response.data.success) {
          this.currentForm = {
            id: response.data.form.id,
            slug: response.data.form.slug,
            title: response.data.form.title,
            description: response.data.form.description || '',
            fields: response.data.form.fields || [],
            theme_color: response.data.form.theme_color || '#17803d',
            redirect_url: response.data.form.redirect_url || null,
            success_message: response.data.form.success_message || 'Thank you for your submission!',
            is_active: response.data.form.is_active !== undefined ? response.data.form.is_active : true
          }

          // Cache the data
          saveToStorage(`form_${formId}`, {
            form: this.currentForm,
            fields: this.currentForm.fields || []
          })
        }
      } catch (error) {
        console.error('Error loading form:', error)
        let errorMessage = 'Failed to load form'

        if (error.response) {
          if (error.response.status === 404) {
            errorMessage = 'Form not found. It may have been deleted.'
          } else if (error.response.status === 401 || error.response.status === 403) {
            errorMessage = 'You do not have permission to view this form.'
          } else if (error.response.status === 500) {
            errorMessage = 'Server error. Please try again later.'
          } else if (error.response.data?.message || error.response.data?.error) {
            errorMessage = error.response.data.message || error.response.data.error
          }
        } else if (error.message) {
          if (error.message.includes('Network') || error.message.includes('timeout')) {
            errorMessage = 'Network error. Please check your connection and try again.'
          } else {
            errorMessage = error.message
          }
        }

        if (window.$toast) {
          window.$toast.error(errorMessage)
        }
      } finally {
        this.loadingForm = false
      }
    },
    async createForm() {
      if (!this.newFormTitle || this.creating) return

      this.creating = true
      this.loading = true
      try {
        const response = await this.axios.post(this.route('lead-forms.store'), {
          title: this.newFormTitle,
          description: '',
          fields: [], // Empty array - always include this
          theme_color: '#17803d',
          success_message: 'Thank you for your submission!',
          redirect_url: null
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        })

        if (response.data && response.data.success) {
          this.currentForm = {
            id: response.data.form.id,
            slug: response.data.form.slug,
            title: response.data.form.title,
            description: response.data.form.description || '',
            fields: response.data.form.fields || [],
            theme_color: response.data.form.theme_color || '#17803d',
            redirect_url: response.data.form.redirect_url || null,
            success_message: response.data.form.success_message || 'Thank you for your submission!',
            is_active: response.data.form.is_active !== undefined ? response.data.form.is_active : true
          }
          this.formsList.push({
            id: response.data.form.id,
            slug: response.data.form.slug,
            title: response.data.form.title,
            views: 0,
            submissions: 0,
            conversion_rate: 0,
            created_at: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          })
          // Update cache
          saveToStorage('forms_list', this.formsList)
          this.showCreateModal = false
          this.newFormTitle = ''
        }
      } catch (error) {
        console.error('Error creating form:', error)
        let errorMessage = 'Failed to create form'

        if (error.response?.data?.errors) {
          // Show validation errors
          const errors = error.response.data.errors
          const errorList = Object.keys(errors).map(key => `${key}: ${errors[key].join(', ')}`).join('\n')
          errorMessage = 'Validation failed:\n' + errorList
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error.message) {
          errorMessage = error.message
        }

        if (window.$toast) {
          window.$toast.error(errorMessage)
        }
      } finally {
        this.creating = false
        this.loading = false
      }
    },
    async saveForm() {
      if (!this.currentForm || this.saving) return Promise.resolve()

      this.saving = true
      try {
        const response = await this.axios.put(
          this.route('lead-forms.update', this.currentForm.id),
          {
            title: this.currentForm.title,
            description: this.currentForm.description,
            fields: this.currentForm.fields,
            theme_color: this.currentForm.theme_color,
            redirect_url: this.currentForm.redirect_url,
            success_message: this.currentForm.success_message,
            is_active: this.currentForm.is_active
          }
        )

        if (response.data && response.data.success) {
          this.currentForm = response.data.form
          const index = this.formsList.findIndex(f => f.id === this.currentForm.id)
          if (index !== -1) {
            this.formsList[index] = {
              ...this.formsList[index],
              title: this.currentForm.title
            }
          }
          // Update localStorage cache
          if (this.currentForm) {
            saveToStorage(`form_${this.currentForm.id}`, {
              form: this.currentForm,
              fields: this.currentForm.fields || []
            })
          }

          if (window.$toast) {
            window.$toast.success('Form saved successfully!')
          }
        }
      } catch (error) {
        console.error('Error saving form:', error)
        let errorMessage = 'Failed to save form'

        if (error.response) {
          if (error.response.status === 401 || error.response.status === 403) {
            errorMessage = 'Session expired. Please refresh the page and try again.'
          } else if (error.response.status === 422) {
            errorMessage = error.response.data?.message || 'Validation failed. Please check your input.'
          } else if (error.response.status === 500) {
            errorMessage = 'Server error. Your changes may not have been saved. Please try again.'
          } else if (error.response.data?.message || error.response.data?.error) {
            errorMessage = error.response.data.message || error.response.data.error
          }
        } else if (error.message) {
          if (error.message.includes('Network') || error.message.includes('timeout')) {
            errorMessage = 'Network error. Your changes may not have been saved. Please check your connection and try again.'
          } else {
            errorMessage = error.message
          }
        }

        if (window.$toast) {
          window.$toast.error(errorMessage)
        }
      } finally {
        this.saving = false
      }
    },
    addField(fieldType) {
      if (!this.currentForm) {
        if (window.$toast) {
          window.$toast.warning('Please create or select a form first')
        }
        return
      }

      const field = {
        id: 'field_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
        type: fieldType.type,
        label: fieldType.label,
        placeholder: `Enter ${fieldType.label.toLowerCase()}`,
        required: false
      }

      if (!this.currentForm.fields) {
        this.currentForm.fields = []
      }
      this.currentForm.fields.push(field)

      // Update cache
      if (this.currentForm) {
        saveToStorage(`form_${this.currentForm.id}`, {
          form: this.currentForm,
          fields: this.currentForm.fields || []
        })
      }
    },
      removeField(index) {
        if (!this.currentForm || !confirm('Remove this field?')) return
        this.currentForm.fields.splice(index, 1)
        this.hasUnsavedChanges = true
        this.autoSaveForm()
      },
    autoSaveForm() {
      if (!this.currentForm || this.saving) return

      // Clear existing timer
      if (this.autoSaveTimer) {
        clearTimeout(this.autoSaveTimer)
      }

      // Set new timer
      const self = this
      this.autoSaveTimer = setTimeout(async () => {
        self.autoSaving = true
        try {
          await self.saveForm()
          self.lastSaved = new Date()
          self.hasUnsavedChanges = false
        } catch (error) {
          // Error already handled in saveForm
        } finally {
          self.autoSaving = false
        }
      }, 3000) // 3 second debounce
    },
    formatTime(date) {
      if (!date) return ''
      const now = new Date()
      const diff = now - new Date(date)
      const seconds = Math.floor(diff / 1000)
      const minutes = Math.floor(seconds / 60)
      const hours = Math.floor(minutes / 60)

      if (seconds < 60) return 'just now'
      if (minutes < 60) return `${minutes}m ago`
      if (hours < 24) return `${hours}h ago`
      return new Date(date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    },
    viewForm() {
      if (!this.currentForm) return
      window.open(this.route('lead-form.view', this.currentForm.slug), '_blank')
    },
    viewLeads() {
      if (!this.currentForm) return
      // Navigate to leads inbox for this form
      this.$inertia.visit(this.route('lead-forms.leads', this.currentForm.id))
    }
  },
  beforeUnmount() {
    // Clean up timer
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer)
    }
  }
}
</script>

<style scoped>
.lead-form-builder {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
}
</style>

